import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { RouteComponent } from '../../route.component';
import {
  CheckoutStatus,
  StateService,
} from 'src/app/service/state/state.service';
import {
  DEbirthDateValidator,
  email_validation_regexp,
} from 'src/assets/val/regexp';
import {
  UserRegistrationForm,
  UserRegistrationDto,
  UserService,
  UserFormDataVM,
  UserRegistrationVM,
} from 'src/app/service/user-service/user.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { HeaderService } from '@app/service/header/header.service';
import { SelectorItem } from '@app/component/selector/selector.component';
import { Market } from '@app/model/payment-request/payment-request.dto';
import { LoanOption } from '@app/route/shared/LoanOption';
import { DeLoanOptionService } from '@app/service/de-loan-option/de-loan-option.service';
import { ValuePipe } from '@app/shared/pipe/value.pipe';

export interface Country {
  label: string;
  value: string;
  imgSrc?: string;
}

@Component({
  selector: 'app-de-user-registration',
  templateUrl: './de-user-registration.component.html',
  styleUrls: ['../../page-shared.css', './de-user-registration.component.scss'],
})
export class DeUserRegistrationComponent
  extends RouteComponent
  implements OnInit, OnDestroy
{
  countries: Country[] = [
    { label: 'Deutschland', value: 'DE', imgSrc: 'assets/flags/de.svg' },
    { label: 'Österreich', value: 'AT', imgSrc: 'assets/flags/at.svg' },
  ];

  loading: boolean = true;
  form: FormGroup;
  formSubscription: Subscription;
  navigationInProgress: boolean = false;
  showError: boolean = false;
  userRegistrationDto: UserRegistrationDto = null;
  editPreviewData: boolean = false;
  infoIsEditable: boolean;
  displayPhoneNumber: string;
  selectorItems: SelectorItem[] = [];
  noInputDisplayed: boolean;
  inputSize: 'small' | 'medium' | 'large';
  invalidDataInPreview: boolean = false;
  invalidForms: string = '';
  selectedCountry: Country = null;
  userRegistrationVM: UserRegistrationVM;
  currentLoanTermsStr: string;
  modalActive: boolean = false;
  AGBModalActive: boolean = false;
  recurringUser: boolean;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private userService: UserService,
    private loanOptionService: DeLoanOptionService,
    private valuePipe: ValuePipe
  ) {
    super(router, route, stateService, headerService);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.inputSize = event.target.innerWidth < 520 ? 'medium' : 'large';
  }

  async ngOnInit(): Promise<void> {
    if (
      this.state.status === CheckoutStatus.CANCELLED ||
      this.state.status === CheckoutStatus.EXPIRED
    ) {
      return;
    }
    try {
      await this.fetchExistingInfo().then(() => {
        this.setUpForm(this.userRegistrationDto);
      });
    } catch (error) {
      this.handleError(error);
    }

    this.getPreviwData();
    this.phoneString();
    this.infoEditable();
    if (this.userRegistrationVM.countryCode?.value === null) {
      this.setSelectedCountry(this.state.market);
    }

    this.inputSize = window.innerWidth < 520 ? 'medium' : 'large';

    this.prepareModalInformation();
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.formSubscription && this.formSubscription.unsubscribe();
  }

  async routeToCancelUrl(): Promise<void> {
    this.navigationInProgress = true;
    try {
      const resp = await this.userService.cancelPayment();
      this.navigationInProgress = false;
      if (!resp.error) {
        window.location.href = this.state.merchantUrls?.onCancelUrl;
      }
    } catch (error) {
      console.log(error);
    }
  }

  setSelectedCountry(value): void {
    this.form.controls.countryCode.setValue(value);
    this.selectedCountry = this.countries.find(
      (country) => country.value === value
    );
  }

  keyToString(key): string {
    const object = {
      email: 'E-Mail Adresse',
      firstName: 'Vorname',
      familyName: 'Nachname',
      birthDate: 'Geburtsdatum',
      streetName: 'Straße',
      houseNumber: 'Hausnummer',
      zipCode: 'Portleitzahl',
      cityName: 'Stadt/Ort',
      countryCode: 'Land',
    };
    return object[key];
  }

  validatePreviewData(): void {
    const invalidForms = Object.keys(this.form.controls).filter((key) => {
      if (this.form.controls[key].invalid) {
        return key;
      }
    });

    this.invalidForms = invalidForms.reduce(
      (tot, curr) => tot + (tot && ', ') + this.keyToString(curr),
      ''
    );

    this.invalidDataInPreview =
      invalidForms.filter(
        (item) =>
          this.displayInput(this.userRegistrationVM[item]) &&
          !this.userRegistrationVM[item].editable
      ).length > 0;
  }

  saveFormData() {
    if (this.navigationInProgress || this.form.invalid) {
      this.showError = true;
    } else {
      this.editPreviewData = false;
      this.getPreviwData();
    }
  }

  async handleCtaClick(): Promise<void> {
    this.validatePreviewData();
    this.loading = true;

    if (this.navigationInProgress || this.form.invalid) {
      this.showError = true;
    } else {
      this.navigationInProgress = true;
      try {
        await this.userService.registerUser(this.packageUserRegistrationForm());
        await this.navigate();
      } catch (err) {
        this.handleError(err);
      }
    }
    this.loading = false;
    this.navigationInProgress = false;
  }

  phoneString(): void {
    const phoneNumberString = this.userRegistrationVM.phoneNumber.value;
    if (!phoneNumberString) {
      return;
    }

    this.displayPhoneNumber = `${phoneNumberString.slice(
      0,
      4
    )} • • • ${phoneNumberString.slice(7)}`;
  }

  infoEditable(): void {
    const values = Object.values(this.userRegistrationVM);
    this.infoIsEditable = values
      .map((item) => !this.displayInput(item) && item.editable)
      .includes(true);
  }

  packageUserRegistrationForm(): UserRegistrationForm {
    const birthdate = this.form.controls.birthDate.value;
    const day = birthdate.slice(0, 2);
    const month = birthdate.slice(3, 5);
    const year = birthdate.slice(6, 10);
    const isoBirthDate = new Date(`${year}-${month}-${day}`).toISOString();

    return {
      firstName: this.form.controls.firstName.value.trim(),
      familyName: this.form.controls.familyName.value.trim(),
      streetName: this.form.controls.streetName.value.trim(),
      houseNumber: this.form.controls.houseNumber.value.trim(),
      zipCode: this.form.controls.zipCode.value.trim(),
      cityName: this.form.controls.cityName.value.trim(),
      countryCode: this.form.controls.countryCode.value.trim(),
      birthDate: isoBirthDate,
      email: this.form.controls.email.value.toLowerCase().trim(),
    };
  }

  getPreviewBackgroundColor(): void {
    const UserRegistrationDataVM = Object.values(this.userRegistrationVM);
    this.noInputDisplayed =
      UserRegistrationDataVM.filter((item) => {
        return this.displayInput(item);
      }).length === 0;
  }

  async fetchExistingInfo(): Promise<void> {
    try {
      this.userRegistrationDto = await this.userService.getActiveUserInfoDe();
      this.recurringUser =
        this.userRegistrationDto.firstName != null &&
        this.userRegistrationDto.birthDate != null;
    } catch (err) {
      this.handleError(err);
    }
  }

  abortEditInfo(): void {
    this.setUpForm(this.userRegistrationDto);
    this.editPreviewData = false;
    this.getPreviewBackgroundColor();
  }

  setModalActive(value: boolean): void {
    this.modalActive = value;
  }

  getPreviwData(): void {
    this.userRegistrationVM = {
      birthDate: {
        value: this.form.controls.birthDate.value,
        editable: this.userRegistrationDto.mayEditBirthDate,
      },
      countryCode: {
        value: this.form.controls.countryCode.value,
        editable: this.userRegistrationDto.countryCode.editable,
      },
      cityName: {
        value: this.form.controls.cityName.value,
        editable: this.userRegistrationDto.cityName.editable,
      },
      email: {
        value: this.form.controls.email.value,
        editable: this.userRegistrationDto.email.editable,
      },
      familyName: {
        value: this.form.controls.familyName.value,
        editable: this.userRegistrationDto.familyName.editable,
      },
      firstName: {
        value: this.form.controls.firstName.value,
        editable: this.userRegistrationDto.firstName.editable,
      },
      houseNumber: {
        value: this.form.controls.houseNumber.value,
        editable: this.userRegistrationDto.houseNumber.editable,
      },
      phoneNumber: {
        value: this.userRegistrationDto.phoneNumber.value,
        editable: this.userRegistrationDto.phoneNumber.editable,
      },
      streetName: {
        value: this.form.controls.streetName.value,
        editable: this.userRegistrationDto.streetName.editable,
      },
      zipCode: {
        value: this.form.controls.zipCode.value,
        editable: this.userRegistrationDto.zipCode.editable,
      },
    };

    this.getPreviewBackgroundColor();
  }

  editData() {
    this.editPreviewData = true;
    this.getPreviewBackgroundColor();
  }

  displayInput(userForm: UserFormDataVM) {
    return (userForm.editable && this.editPreviewData) || !userForm.value;
  }

  setUpForm(dto: UserRegistrationDto): void {
    const textValidators = [Validators.required, Validators.maxLength(100)];

    const numberValidators = [
      Validators.required,
      Validators.pattern('^[0-9]*$'),
    ];

    this.form = new FormGroup({
      email: new FormControl(
        dto.email.value,
        dto.email.editable && [
          Validators.required,
          Validators.pattern(email_validation_regexp),
        ]
      ),
      firstName: new FormControl(
        dto.firstName.value,
        dto.firstName.editable && textValidators
      ),
      familyName: new FormControl(
        dto.familyName.value,
        dto.familyName.editable && textValidators
      ),
      birthDate: new FormControl(dto.birthDate, [
        Validators.required,
        Validators.pattern(DEbirthDateValidator),
      ]),
      streetName: new FormControl(
        dto.streetName.value,
        dto.streetName.editable && [...textValidators]
      ),
      houseNumber: new FormControl(
        dto.houseNumber.value,
        dto.houseNumber.editable && [Validators.required]
      ),
      zipCode: new FormControl(
        dto.zipCode.value && dto.zipCode.value.replace(/\s/g, ''),
        dto.zipCode.editable && [
          ...numberValidators,
          Validators.minLength(4),
          Validators.maxLength(5),
        ]
      ),
      cityName: new FormControl(
        dto.cityName.value,
        dto.cityName.editable && textValidators
      ),
      countryCode: new FormControl(
        dto.countryCode.value,
        dto.countryCode.editable && [Validators.required]
      ),
    });

    this.selectedCountry = this.countries.find(
      (country) => country.value === this.form.controls.countryCode.value
    );

    this.formSubscription = this.form.controls.birthDate.valueChanges.subscribe(
      (value: string) => {
        let valueArray = value.replace(/[^0-9]/g, '').split('');
        const insert = (arr: string[], index: number, value: string) =>
          (arr = [...arr.slice(0, index), value, ...arr.slice(index)]);

        if (valueArray.length >= 2) {
          valueArray = insert(valueArray, 2, '.');
        }

        if (valueArray.length >= 5) {
          valueArray = insert(valueArray, 5, '.');
        }

        const formattedValue = valueArray.join('');
        if (formattedValue) {
          this.form.patchValue(
            { birthDate: formattedValue },
            { emitEvent: false }
          );
        }
      }
    );
  }

  toggleAGBModal(): void {
    this.AGBModalActive = !this.AGBModalActive;
  }

  prepareModalInformation(): void {
    this.selectorItems = [
      {
        tab: 'Ihr Kauf',
        title: 'Zusammenfassung',
        lineItems: this.state.paymentInformation?.lineItems,
        totalValue: this.stateService.getCustomTaxTypeText(
          `Gesamtbetrag inkl. MwSt: ${this.valuePipe.transform(
            this.state.totalCheckoutValue
          )}`,
          this.state.checkoutCustomizations
        ),
      },
    ];

    if (
      this.state.loanOptions &&
      this.state.loanOptions.length > 0 &&
      this.state.loanOptions[0]?.termId
    ) {
      const selectedLoanOption = this.state.loanOptions.find(
        (loanOption: LoanOption) => loanOption.termId
      );

      if (selectedLoanOption) {
        const selectedLoanOptionVM = this.loanOptionService.transformDtoTOVM(
          [selectedLoanOption],
          () => this.toggleAGBModal()
        );

        this.currentLoanTermsStr = this.loanOptionService.buildLoanTermsString(
          selectedLoanOptionVM[0],
          this.state.totalCheckoutValue
        );

        this.selectorItems = [
          ...this.selectorItems,
          {
            tab: 'Zahlungsbedingungen',
            title: 'Bedingungen für den Kauf auf Raten',
            descriptions: [{ text: this.currentLoanTermsStr }],
            links: [
              {
                text: 'Bedingungen für Teilzahlungen',
                onClick: () =>
                  this.openURLinNewWindow(selectedLoanOption.loanTermsURL),
              },
              {
                ...(this.state.checkoutCustomizations.merchantTermsUrl !==
                  null &&
                  this.state.checkoutCustomizations.merchantName !== null && {
                    text: this.stateService.getCustomTermsUrlText(
                      this.state.checkoutCustomizations.merchantName + ' AGB',
                      this.state.checkoutCustomizations
                    ),
                    onClick: () =>
                      this.openURLinNewWindow(
                        this.state.checkoutCustomizations.merchantTermsUrl
                      ),
                  }),
              },
              {
                ...(this.state.market === Market.AT && {
                  text: 'Europäische Standardinformationen für Kreditierungen nach dem Verbraucherkreditgesetz',
                  onClick: () =>
                    this.openURLinNewWindow(selectedLoanOption.secciURL),
                }),
              },
            ],
          },
        ];
      }
    }
  }

  openURLinNewWindow(url: string): void {
    window.open(url, '_blank');
  }

  deactivateAGBModal(): void {
    this.AGBModalActive = false;
  }
}

const germanAddress = /^[A-Za-zÄäÖöÜüß\s.,()]+$/;
